/**
 * AddForm Component
 *
 * Form for creating new bookings. Supports:
 * - Hard bookings (project-based)
 * - Soft bookings (customer-based)
 * - Absence bookings
 *
 * Flow:
 * 1. User selects project/customer
 * 2. User sets hours with slider/input
 * 3. User clicks "Continue" to check conflicts
 * 4. ConflictResolver shows if conflicts exist
 * 5. User resolves conflicts and saves
 */

import React, { useCallback, useMemo, useState } from "react";
import styles from "./AddForm.module.css";

// Hooks
import { useBookingMutations } from "@/hooks/booking/useBookingMutations";
import { useConflictResolution } from "@/hooks/booking/useConflictResolution";
import { useCustomers } from "@/hooks/data/useCustomers";
import { useProjects } from "@/hooks/data/useProjects";
import type { BookingCard } from "@/hooks/booking/useBookings";
import { useUser } from "@/hooks/auth/useUser";

// Types
import type { SelectOption } from "@/components/ui/forms/Select/types";
import type { AddFormProps, AddFormState } from "./types";
import { isCustomerTarget, isEmployeeTarget } from "../shared";

// Utils - select option converters
import {
  toProjectOptions,
  toCustomerOptions,
  selectedProjectOption as toSelectedProjectOption,
  selectedCustomerOption as toSelectedCustomerOption,
} from "@/utils/selectOptions";

// Utils - form logic (pure functions)
import {
  hasValidTarget as checkHasValidTarget,
  checkOverbookedDays,
  countWeekendDays,
  countHolidayDays,
  countWarningRedDays,
  filterRedDays,
  hasSingleDayFullAbsence,
  getAbsenceConflictMessage,
  buildPostBookings,
  clampHours,
} from "./utils";

// Constants
import {
  DEFAULT_HOURS,
  MAX_HOURS_OVERTIME,
  MAX_HOURS_DAY,
} from "@/constants/booking";

// Shared Components
import {
  FormContainer,
  FormHeader,
  FormActions,
  SavingStep,
  HoursInput,
  ProjectSelector,
  CustomerSelector,
  AbsenceCheckbox,
} from "../shared";

// UI Components
import { Modal } from "@/components/ui/feedback/Modal";
import { ActionButton } from "@/components/ui/buttons/ActionButton";
import { Icon } from "@/components/ui/display/Icon";

// Step Components
import { ConflictStep } from "./steps";

export const AddForm: React.FC<AddFormProps> = ({
  targetEntity,
  selectedCards,
  bookingType = "hard",
  defaultHours = DEFAULT_HOURS,
  absenceDisabled = false,
  onDone,
  onError,
  className,
  "data-testid": testId,
}) => {
  // ============================================================
  // DATA HOOKS
  // ============================================================
  const { activeProjects, isLoading: projectsLoading } = useProjects();
  const { activeCustomers, isLoading: customersLoading } = useCustomers();
  const { createBookingsRaw } = useBookingMutations();
  const conflictResolution = useConflictResolution(defaultHours);
  const { organizationSettings } = useUser();

  // ============================================================
  // FORM STATE
  // ============================================================
  const [state, setState] = useState<AddFormState>({
    step: "input",
    project: null,
    customer: isCustomerTarget(targetEntity) ? targetEntity : null,
    hours: defaultHours,
    isAbsence: false,
    allowOvertime: false,
    absenceSummary: { skippedDates: [], partialAdjustments: [] },
  });

  const [showWeekendHolidayDialog, setShowWeekendHolidayDialog] =
    useState(false);

  // ============================================================
  // DERIVED STATE
  // ============================================================
  const isHardBooking = bookingType === "hard" && !state.isAbsence;
  const isSoftBooking = bookingType === "soft" && !state.isAbsence;
  const isTargetCustomer = isCustomerTarget(targetEntity);
  const isTargetEmployee = isEmployeeTarget(targetEntity);
  const isCustomerOnlySoftBooking = isSoftBooking && isTargetCustomer;

  // ============================================================
  // COMPUTED VALUES
  // ============================================================
  const maxHours = useMemo<number | undefined>(() => {
    if (isCustomerOnlySoftBooking) return undefined;

    return state.allowOvertime ? MAX_HOURS_OVERTIME : defaultHours;
  }, [isCustomerOnlySoftBooking, state.allowOvertime, defaultHours]);
  const totalDays = selectedCards.length;
  const totalHours = totalDays * state.hours;
  const totalHoursText = Number.isInteger(totalHours)
    ? String(totalHours)
    : totalHours.toFixed(1);

  const weekendDays = useMemo(
    () => countWeekendDays(selectedCards),
    [selectedCards],
  );

  const holidayDays = useMemo(
    () => countHolidayDays(selectedCards),
    [selectedCards],
  );

  const canSkipWeekends =
    weekendDays > 0 && !organizationSettings.include_weekends;

  const canSkipHolidays =
    holidayDays > 0 && !organizationSettings.include_holidays;

  const canSkipRedDays = canSkipWeekends || canSkipHolidays;

  const skipButtonLabel = useMemo(() => {
    if (canSkipWeekends && canSkipHolidays) {
      return "Hopp over helger og helligdager";
    }
    if (canSkipWeekends) {
      return "Hopp over helger";
    }
    if (canSkipHolidays) {
      return "Hopp over helligdager";
    }
    return "";
  }, [canSkipWeekends, canSkipHolidays]);

  const warningWeekendDays = canSkipWeekends ? weekendDays : 0;
  const warningHolidayDays = canSkipHolidays ? holidayDays : 0;

  const warningRedDayCount = useMemo(
    () =>
      countWarningRedDays(selectedCards, {
        skipWeekends: canSkipWeekends,
        skipHolidays: canSkipHolidays,
      }),
    [selectedCards, canSkipWeekends, canSkipHolidays],
  );

  const warningRedDayText = useMemo(() => {
    const hasWeekends = warningWeekendDays > 0;
    const hasHolidays = warningHolidayDays > 0;

    if (hasWeekends && hasHolidays) {
      return `${warningRedDayCount} helgedager eller helligdager`;
    }
    if (hasWeekends) {
      return `${warningRedDayCount} helgedag${warningRedDayCount === 1 ? "" : "er"}`;
    }
    if (hasHolidays) {
      return `${warningRedDayCount} helligdag${warningRedDayCount === 1 ? "" : "er"}`;
    }
    return `${warningRedDayCount} dag${warningRedDayCount === 1 ? "" : "er"}`;
  }, [warningWeekendDays, warningHolidayDays, warningRedDayCount]);

  const warningDialogTitle = useMemo(() => {
    const hasWeekends = warningWeekendDays > 0;
    const hasHolidays = warningHolidayDays > 0;

    if (hasWeekends && hasHolidays) {
      return "Booking inkluderer helger og helligdager";
    }
    if (hasWeekends) {
      return "Booking inkluderer helger";
    }
    if (hasHolidays) {
      return "Booking inkluderer helligdager";
    }
    return "Booking inkluderer ikke-arbeidsdager";
  }, [warningWeekendDays, warningHolidayDays]);

  const hasValidTarget = useMemo(
    () =>
      checkHasValidTarget(
        state,
        isHardBooking,
        isSoftBooking,
        isTargetCustomer,
      ),
    [state, isHardBooking, isSoftBooking, isTargetCustomer],
  );

  const singleDayFullAbsence = useMemo(
    () =>
      hasSingleDayFullAbsence(
        state,
        bookingType,
        isTargetEmployee,
        selectedCards,
        defaultHours,
      ),
    [state, bookingType, isTargetEmployee, selectedCards, defaultHours],
  );

  const absenceConflictMessage = useMemo(
    () =>
      getAbsenceConflictMessage(
        state,
        bookingType,
        isTargetEmployee,
        selectedCards,
        defaultHours,
      ),
    [state, bookingType, isTargetEmployee, selectedCards, defaultHours],
  );

  // ============================================================
  // SELECT OPTIONS
  // ============================================================
  const projectOptions = useMemo(
    () => toProjectOptions(activeProjects),
    [activeProjects],
  );
  const customerOptions = useMemo(
    () => toCustomerOptions(activeCustomers),
    [activeCustomers],
  );
  const selectedProjectOption = useMemo(
    () => toSelectedProjectOption(state.project),
    [state.project],
  );
  const selectedCustomerOption = useMemo(
    () => toSelectedCustomerOption(state.customer),
    [state.customer],
  );

  // ============================================================
  // HANDLERS - Form Fields
  // ============================================================
  const handleProjectChange = useCallback(
    (selected: SelectOption<number>[]) => {
      const project =
        selected.length > 0
          ? (activeProjects.find((p) => p.id === selected[0].id) ?? null)
          : null;
      setState((prev) => ({ ...prev, project }));
    },
    [activeProjects],
  );

  const handleCustomerChange = useCallback(
    (selected: SelectOption<number>[]) => {
      const customer =
        selected.length > 0
          ? (activeCustomers.find((c) => c.id === selected[0].id) ?? null)
          : null;
      setState((prev) => ({ ...prev, customer }));
    },
    [activeCustomers],
  );

  const handleHoursChange = useCallback(
    (hours: number) => {
      setState((prev) => ({
        ...prev,
        hours: clampHours(hours, maxHours),
      }));
    },
    [maxHours],
  );

  const handleHoursInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(e.target.value);

      if (!isNaN(value)) {
        setState((prev) => ({
          ...prev,
          hours: clampHours(value, maxHours),
        }));
      }
    },
    [maxHours],
  );

  const handleAbsenceToggle = useCallback(() => {
    setState((prev) => {
      const newIsAbsence = !prev.isAbsence;

      return {
        ...prev,
        isAbsence: newIsAbsence,
        allowOvertime: newIsAbsence ? false : prev.allowOvertime,
      };
    });
  }, []);

  const handleOvertimeToggle = useCallback(() => {
    if (isCustomerOnlySoftBooking) return;

    setState((prev) => {
      const newAllowOvertime = !prev.allowOvertime;
      const newHours = newAllowOvertime
        ? prev.hours
        : Math.min(prev.hours, defaultHours);
      return { ...prev, allowOvertime: newAllowOvertime, hours: newHours };
    });
  }, [defaultHours, isCustomerOnlySoftBooking]);

  // ============================================================
  // HANDLERS - Submission
  // ============================================================
  // Helper function to proceed with submission (used by both direct submit and dialog actions)
  const proceedWithSubmission = useCallback(
    async (cardsToBook: BookingCard[] = selectedCards) => {
      // Validate overbooking against the final card set (supports "skip weekends/holidays")
      if (
        checkOverbookedDays(
          bookingType,
          cardsToBook,
          state.hours,
          MAX_HOURS_DAY,
        )
      ) {
        onError?.(
          "En eller flere dager har for mange bookede timer. Maksimum er 24 timer per dag.",
        );
        return;
      }

      const { posts: postBookings, absenceSummary } = buildPostBookings(
        state,
        cardsToBook,
        { targetEntity, isTargetEmployee, isTargetCustomer, isSoftBooking },
        defaultHours,
      );

      if (postBookings.length === 0) {
        onError?.(
          "Ingen bookinger ble opprettet. Alle valgte dager kan ha fullt fravær.",
        );
        return;
      }

      setState((prev) => ({ ...prev, step: "conflict", absenceSummary }));
      await conflictResolution.startResolution(postBookings, {
        autoResolve: state.isAbsence || !absenceConflictMessage,
      });
    },
    [
      bookingType,
      selectedCards,
      state,
      targetEntity,
      isTargetEmployee,
      isTargetCustomer,
      isSoftBooking,
      defaultHours,
      conflictResolution,
      absenceConflictMessage,
      onError,
    ],
  );

  const handleSubmit = useCallback(async () => {
    if (warningRedDayCount > 0) {
      setShowWeekendHolidayDialog(true);
      return;
    }

    await proceedWithSubmission();
  }, [warningRedDayCount, proceedWithSubmission]);

  // ============================================================
  // HANDLERS - Weekend/Holiday Dialog
  // ============================================================
  const handleDialogCancel = useCallback(() => {
    setShowWeekendHolidayDialog(false);
  }, []);

  const handleBookAnyway = useCallback(async () => {
    setShowWeekendHolidayDialog(false);
    await proceedWithSubmission(selectedCards);
  }, [proceedWithSubmission, selectedCards]);

  const handleSkipRedDays = useCallback(async () => {
    setShowWeekendHolidayDialog(false);

    const filteredCards = filterRedDays(selectedCards, {
      skipWeekends: canSkipWeekends,
      skipHolidays: canSkipHolidays,
    });

    if (filteredCards.length === 0) {
      onError?.(
        "Ingen dager å booke etter å ha fjernet relevante helger og/eller helligdager.",
      );
      return;
    }

    await proceedWithSubmission(filteredCards);
  }, [
    proceedWithSubmission,
    selectedCards,
    onError,
    canSkipWeekends,
    canSkipHolidays,
  ]);

  const handleConflictCancel = useCallback(() => {
    conflictResolution.cancelResolution();
    setState((prev) => ({
      ...prev,
      step: "input",
      absenceSummary: { skippedDates: [], partialAdjustments: [] },
    }));
  }, [conflictResolution]);

  const handleConflictResolve = useCallback(async () => {
    setState((prev) => ({ ...prev, step: "saving" }));

    try {
      const postsToSave = conflictResolution.completeResolution();
      if (postsToSave.length === 0) {
        onError?.("Ingen bookinger å lagre etter konfliktløsning.");
        setState((prev) => ({ ...prev, step: "input" }));
        return;
      }

      await createBookingsRaw(postsToSave);
      onDone?.("Bookinger lagret");
    } catch (error) {
      const message =
        error instanceof Error && error.message === "FORBIDDEN"
          ? "Ikke autorisert til å lagre endringer"
          : "Kunne ikke lagre endringer";
      onError?.(message);
      setState((prev) => ({ ...prev, step: "input" }));
    }
  }, [conflictResolution, createBookingsRaw, onDone, onError]);

  // ============================================================
  // RENDER - Step-based
  // ============================================================
  if (state.step === "saving") {
    return (
      <SavingStep
        message="Lagrer bookinger..."
        className={className}
        data-testid={testId}
        loadingTestId="add-form-saving"
      />
    );
  }

  if (state.step === "conflict") {
    return (
      <ConflictStep
        conflictResolution={conflictResolution}
        absenceSummary={state.absenceSummary}
        onCancel={handleConflictCancel}
        onResolve={handleConflictResolve}
        className={className}
        data-testid={testId}
      />
    );
  }

  // ============================================================
  // RENDER - Input Step
  // ============================================================
  const showProjectSelector = isHardBooking;
  const showCustomerSelector = isSoftBooking && !isTargetCustomer;
  const showAbsenceCheckbox = bookingType === "hard" && !absenceDisabled;
  const showFullHoursInput =
    isHardBooking || (isSoftBooking && !isTargetCustomer) || state.isAbsence;
  const submitDisabled =
    !hasValidTarget || state.hours <= 0 || singleDayFullAbsence;

  return (
    <>
      <FormContainer
        className={className}
        data-testid={testId}
        contentTestId="add-form-input"
      >
        <FormHeader data-testid="add-form-total-summary">
          Totalt{" "}
          <span className={styles.totalHoursValue}>{totalHoursText}</span> timer
          fordelt på {totalDays} {totalDays === 1 ? "dag" : "dager"}.
        </FormHeader>

        {showProjectSelector && (
          <ProjectSelector
            label="Prosjekt"
            options={projectOptions}
            value={selectedProjectOption}
            onChange={handleProjectChange}
            loading={projectsLoading}
            knownCustomers={activeCustomers}
            testId="add-form-project"
          />
        )}

        {showCustomerSelector && (
          <CustomerSelector
            label="Kunde"
            options={customerOptions}
            value={selectedCustomerOption}
            onChange={handleCustomerChange}
            loading={customersLoading}
            testId="add-form-customer"
          />
        )}

        {showAbsenceCheckbox && (
          <AbsenceCheckbox
            checked={state.isAbsence}
            onChange={handleAbsenceToggle}
            testIdPrefix="add-form"
          />
        )}

        <HoursInput
          variant={showFullHoursInput ? "full" : "simple"}
          value={state.hours}
          maxHours={maxHours}
          defaultHours={defaultHours}
          allowOvertime={state.allowOvertime}
          onChange={handleHoursChange}
          onInputChange={handleHoursInputChange}
          onOvertimeToggle={state.isAbsence ? undefined : handleOvertimeToggle}
          testIdPrefix="add-form"
        />

        {absenceConflictMessage && (
          <p
            data-testid="add-form-absence-warning"
            style={{ color: "var(--color-warning)", fontSize: "0.85rem" }}
          >
            {absenceConflictMessage}
          </p>
        )}

        <FormActions
          submitLabel="Fortsett"
          submitIcon="check"
          onSubmit={handleSubmit}
          submitDisabled={submitDisabled}
          testIdPrefix="add-form"
        />
      </FormContainer>

      <Modal
        isOpen={showWeekendHolidayDialog}
        title={warningDialogTitle}
        onClose={handleDialogCancel}
        size="lg"
        data-testid="add-form-weekend-holiday-dialog"
      >
        <div style={{ padding: "1rem" }}>
          <p style={{ marginBottom: "1.5rem" }}>
            Du har valgt {warningRedDayText}. Vil du fortsette med å booke disse
            dagene, eller hoppe over dem som ikke skal inkluderes?
          </p>

          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              justifyContent: "flex-end",
            }}
          >
            <ActionButton
              label="Avbryt"
              variant="danger"
              icon={<Icon name="x" size="sm" aria-hidden />}
              iconPosition="left"
              onClick={handleDialogCancel}
              data-testid="add-form-weekend-dialog-cancel"
            />
            {canSkipRedDays && (
              <ActionButton
                label={skipButtonLabel}
                variant="secondary"
                icon={<Icon name="skip-forward" size="sm" aria-hidden />}
                iconPosition="left"
                onClick={handleSkipRedDays}
                data-testid="add-form-weekend-dialog-skip"
              />
            )}
            <ActionButton
              label="Book uansett"
              variant="primary"
              icon={<Icon name="check" size="sm" aria-hidden />}
              iconPosition="left"
              onClick={handleBookAnyway}
              data-testid="add-form-weekend-dialog-confirm"
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

AddForm.displayName = "AddForm";

export default AddForm;
